import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Users,
  Mail,
  Phone,
  IdCard,
  ShieldCheck,
  CheckCircle,
  XCircle,
} from "lucide-react";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";

const RecruiterList = () => {
  const navigate = useNavigate();
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRecruiters = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${apiurl}/api/user/getbyrole`, {
        role: "recruiter",
        status: "active",
      });

      if (response.data.success) {
        setRecruiters(response.data.users || []);
      }
    } catch (err) {
      console.error("Error fetching recruiters:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecruiters();
  }, []);

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="max-w-7xl mx-auto mt-8 bg-white rounded-2xl border shadow-sm">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b">
            <div>
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <Users size={22} /> Recruiters
              </h2>
              <p className="text-sm text-gray-500">
                Active recruiters with HRMS access
              </p>
            </div>

            <button
              onClick={() => navigate("/recruiters/create-recruiter")}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
            >
              <Plus size={18} />
              Create Recruiter
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-gray-600">
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Employee ID</th>
                  <th className="px-6 py-3">Contact</th>
                  <th className="px-6 py-3">Verification</th>
                  <th className="px-6 py-3">2FA</th>
                  <th className="px-6 py-3">Portal</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Created</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="8" className="text-center py-10 text-gray-500">
                      Loading recruiters...
                    </td>
                  </tr>
                )}

                {!loading && recruiters.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-10 text-gray-500">
                      No recruiters found
                    </td>
                  </tr>
                )}

                {recruiters.map((rec) => (
                  <tr
                    key={rec._id}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    {/* Name */}
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {rec.firstName} {rec.lastName}
                    </td>

                    {/* Employee ID */}
                    <td className="px-6 py-4 flex items-center gap-1">
                      <IdCard size={14} />
                      {rec.employeeDetails?.employeeId || "-"}
                    </td>

                    {/* Contact */}
                    <td className="px-6 py-4 space-y-1">
                      <div className="flex items-center gap-1">
                        <Mail size={14} /> {rec.email}
                      </div>
                      <div className="flex items-center gap-1">
                        <Phone size={14} /> {rec.phone || "-"}
                      </div>
                    </td>

                    {/* Verification */}
                    <td className="px-6 py-4 text-xs space-y-1">
                      <StatusBadge
                        label="Email"
                        value={rec.verificationDetails?.isEmailVerified}
                      />
                      <StatusBadge
                        label="Phone"
                        value={rec.verificationDetails?.isPhoneVerified}
                      />
                    </td>

                    {/* 2FA */}
                    <td className="px-6 py-4">
                      {rec.twoFactorAuth?.enabled ? (
                        <span className="flex items-center gap-1 text-green-600 text-xs">
                          <ShieldCheck size={14} /> Enabled
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">Disabled</span>
                      )}
                    </td>

                    {/* Portal */}
                    <td className="px-6 py-4 text-xs">
                      {rec.portal?.join(", ") || "-"}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 text-xs rounded-full bg-green-100 text-green-700">
                        Active
                      </span>
                    </td>

                    {/* Created */}
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {new Date(rec.createdAt).toLocaleDateString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Container>
    </Layout>
  );
};

/* ---------- Small Components ---------- */

const StatusBadge = ({ label, value }) => (
  <div className="flex items-center gap-1">
    {value ? (
      <CheckCircle size={12} className="text-green-600" />
    ) : (
      <XCircle size={12} className="text-red-500" />
    )}
    <span>{label}</span>
  </div>
);

export default RecruiterList;